import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  useCreateNecOfficeMutation,
  useDeleteNecOfficeMutation,
  useGetNecOfficesQuery,
} from "../api/necOfficeApi";

type UiMsg = { type: "success" | "error"; text: string } | null;

function msgFromErr(err: any) {
  return err?.data?.message || err?.error || "Request failed";
}

export default function NecOfficesPage() {
  const { data = [], isLoading, error, refetch } = useGetNecOfficesQuery();

  const [createOffice, { isLoading: creating }] = useCreateNecOfficeMutation();
  const [deleteOffice, { isLoading: deleting }] = useDeleteNecOfficeMutation();

  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<UiMsg>(null);

  const [form, setForm] = useState({ name: "", description: "", rank: "" });
  const [formErr, setFormErr] = useState<{ name?: string }>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) setTimeout(() => nameRef.current?.focus(), 0);
  }, [open]);

  const list = useMemo(() => {
    return [...data].sort(
      (a: any, b: any) => (a.rank ?? 9999) - (b.rank ?? 9999)
    );
  }, [data]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!form.name.trim()) {
      setFormErr({ name: "Name is required" });
      nameRef.current?.focus();
      return;
    }

    try {
      const res: any = await createOffice({
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        rank: form.rank ? Number(form.rank) : undefined,
      }).unwrap();

      setMsg({
        type: "success",
        text: res?.message || "Office created successfully",
      });
      setOpen(false);
      setForm({ name: "", description: "", rank: "" });
      setFormErr({});
    } catch (err: any) {
      setMsg({ type: "error", text: msgFromErr(err) });
    }
  };

  const onDelete = async (id: number) => {
    const ok = window.confirm(
      "Delete this office? This also removes its role mapping."
    );
    if (!ok) return;

    setMsg(null);
    try {
      const res: any = await deleteOffice(id).unwrap();
      setMsg({ type: "success", text: res?.message || "Office deleted" });
    } catch (err: any) {
      setMsg({ type: "error", text: msgFromErr(err) });
    }
  };

  const busy = creating || deleting;

  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">NEC Offices</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage NEC offices (auto-creates a role with slug).
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>
          <button
            onClick={() => {
              setMsg(null);
              setOpen(true);
            }}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
          >
            + New Office
          </button>
        </div>
      </div>

      {msg ? (
        <div
          className={`mt-4 rounded-lg border p-3 text-sm ${
            msg.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      ) : null}

      {isLoading ? (
        <div className="mt-6 text-gray-600">Loading offices...</div>
      ) : error ? (
        <div className="mt-6 text-red-600">Failed to load offices.</div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Rank</th>
                <th className="py-2">Office</th>
                <th className="py-2">Slug</th>
                <th className="py-2">Role</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((o: any) => (
                <tr key={o.id} className="border-b">
                  <td className="py-3 text-gray-700">{o.rank ?? "—"}</td>
                  <td className="py-3 font-semibold text-gray-800">{o.name}</td>
                  <td className="py-3 text-gray-600">{o.slug ?? "—"}</td>
                  <td className="py-3 text-gray-600">
                    {o.role?.name ?? o.role_id ?? "—"}
                  </td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/nec-offices/${o.id}`}
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                      >
                        Details
                      </Link>

                      <button
                        disabled={busy}
                        onClick={() => onDelete(o.id)}
                        className="px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50 text-xs disabled:opacity-60"
                      >
                        {deleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!list.length ? (
                <tr>
                  <td colSpan={5} className="py-6 text-gray-500">
                    No offices found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Create Office</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Creating an office auto-creates a role using the slug.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                ✕
              </button>
            </div>

            <form onSubmit={submit} className="mt-4 space-y-3">
              <div>
                <label className="text-sm text-gray-600">Name</label>
                <input
                  ref={nameRef}
                  value={form.name}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, name: e.target.value }));
                    if (formErr.name) setFormErr({});
                  }}
                  className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${
                    formErr.name ? "border-red-300" : ""
                  }`}
                  disabled={creating}
                />
                {formErr.name ? (
                  <div className="text-xs text-red-600 mt-1">
                    {formErr.name}
                  </div>
                ) : null}
              </div>

              <div>
                <label className="text-sm text-gray-600">
                  Description (optional)
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  rows={3}
                  disabled={creating}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Rank (optional)</label>
                <input
                  type="number"
                  value={form.rank}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, rank: e.target.value }))
                  }
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  disabled={creating}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
